export type CurrencyKey = 'USD' | 'COP';

export interface CurrencyConfig {
  apiAsset: string;
  decimals: number;
  symbol: string;
  suffix?: string;
  flag: string;
  locale: string;
  label: string;
}

export const CURRENCIES: Record<CurrencyKey, CurrencyConfig> = {
  USD: {
    apiAsset: 'dUSD/6',
    decimals: 6,
    symbol: '$',
    flag: '🇺🇸',
    locale: 'en-US',
    label: 'USD - Dollar',
  },
  COP: {
    apiAsset: 'COP/2',
    decimals: 2,
    symbol: '$',
    suffix: 'COP',
    flag: '🇨🇴',
    locale: 'es-CO',
    label: 'COP - Colombian Peso',
  },
};

export const CURRENCY_KEYS = Object.keys(CURRENCIES) as CurrencyKey[];

export const ASSET_MAP: Record<CurrencyKey, string> = Object.fromEntries(
  CURRENCY_KEYS.map((key) => [key, CURRENCIES[key].apiAsset]),
) as Record<CurrencyKey, string>;

function getMultiplier(key: CurrencyKey): number {
  return 10 ** CURRENCIES[key].decimals;
}

export function toSmallestUnit(amount: number, key: CurrencyKey): number {
  return Math.round(amount * getMultiplier(key));
}

export function fromSmallestUnit(
  smallestUnit: number,
  key: CurrencyKey,
): number {
  return smallestUnit / getMultiplier(key);
}

export function formatAmount(
  amount: number,
  key: CurrencyKey,
  opts?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
): string {
  const config = CURRENCIES[key];
  const minDigits = opts?.minimumFractionDigits ?? (key === 'COP' ? 0 : 2);
  const maxDigits = opts?.maximumFractionDigits ?? (key === 'COP' ? 0 : 2);
  const formatted = amount.toLocaleString(config.locale, {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  });
  return config.suffix
    ? `${config.symbol}${formatted} ${config.suffix}`
    : `${config.symbol}${formatted}`;
}

const API_ASSET_TO_KEY: Record<string, CurrencyKey> = {
  ...Object.fromEntries(
    CURRENCY_KEYS.map((key) => [CURRENCIES[key].apiAsset, key]),
  ),
  'DUSD/6': 'USD',
  'COPM/2': 'COP',
};

export function getCurrencyKeyFromApiAsset(apiAsset: string): CurrencyKey {
  return API_ASSET_TO_KEY[apiAsset] ?? 'USD';
}

export function getDecimalsFromAssetPair(assetPair: string): number {
  const key = API_ASSET_TO_KEY[assetPair];
  if (key) return CURRENCIES[key].decimals;
  const parts = assetPair.split('/');
  return parts[1] ? Number.parseInt(parts[1], 10) : 0;
}

const BALANCE_CODE_TO_KEY: Record<string, CurrencyKey> = {
  DUSD: 'USD',
  COP: 'COP',
};

export function getCurrencyKeyFromBalanceCode(
  code: string,
): CurrencyKey | undefined {
  return BALANCE_CODE_TO_KEY[code];
}
